/* Se il modello si mostra all'operatore.
 *
 * **L'idoneita' arriva dal servizio.** Il tasto e' disabilitato quando
 * `acceptable` e' falso, e `acceptable` non si calcola qui: le soglie sono
 * quelle dell'Allegato 5, sono contrattuali, e riscriverle nella pagina le
 * duplicherebbe. Due copie di una regola sono due regole, e la seconda si
 * scopre il giorno in cui divergono — cioe' quando la schermata dice «si puo'
 * accendere» e il servizio rifiuta, e chi guarda non sa a quale dei due
 * credere.
 *
 * **Le cifre si mostrano accanto alle soglie.** «Non pronto» non dice a nessuno
 * che cosa fare: serve leggere quale metrica manca e di quanto, altrimenti
 * l'unica reazione possibile e' riprovare piu' tardi.
 *
 * **Spegnere non chiede condizioni.** Rimettere in ombra un modello che sta
 * sbagliando non deve aspettare che qualcuno lo dimostri.
 */
import { useEffect, useState } from 'react'

import { ServiceError } from '../../api'
import { hideModel, readModelStanding, showModel, type ModelStanding } from '../../admin/model'
import { notMeasured, share } from '../../format'
import { useLocale, useT } from '../../i18n/LocaleProvider'
import { t as translate } from '../../i18n/t'

export function ModelAdmin(): JSX.Element {
  const t = useT()
  const { locale } = useLocale()
  const [standing, setStanding] = useState<ModelStanding | null>(null)
  const [failure, setFailure] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)

  useEffect(() => {
    let cancelled = false
    void readModelStanding()
      .then((found) => {
        if (!cancelled) setStanding(found)
      })
      .catch((error: unknown) => {
        if (!cancelled) setFailure(read(error, translate(locale, 'model.readFailed')))
      })
    return () => {
      cancelled = true
    }
  }, [locale])

  function act(what: () => Promise<ModelStanding>): void {
    setBusy(true)
    setFailure(null)
    void what()
      .then(setStanding)
      .catch((error: unknown) => {
        setFailure(read(error, t('users.requestFailed')))
        // Si rilegge dopo un rifiuto: le cifre del rifiuto sono le cifre di
        // adesso, e lasciare a schermo quelle di prima farebbe sembrare
        // arbitrario il diniego.
        void readModelStanding()
          .then(setStanding)
          .catch(() => undefined)
      })
      .finally(() => setBusy(false))
  }

  if (failure !== null && standing === null) {
    return (
      <main className="amministrazione">
        <h2>{t('title.model')}</h2>
        <p role="alert">{failure}</p>
      </main>
    )
  }
  if (standing === null) {
    return (
      <main className="amministrazione">
        <h2>{t('title.model')}</h2>
        <p role="status">{t('model.loading')}</p>
      </main>
    )
  }

  return (
    <main className="amministrazione">
      <h2>{t('title.model')}</h2>

      {failure !== null && <p role="alert">{failure}</p>}

      <p>
        {t('model.predictionsLead')}{' '}
        <strong>{standing.on_display ? t('model.shown') : t('model.hidden')}</strong>.
      </p>

      <table>
        <caption>{t('model.metricsCaption', { n: standing.inspections })}</caption>
        <thead>
          <tr>
            <th scope="col">{t('model.colMetric')}</th>
            <th scope="col">{t('model.colMeasured')}</th>
            <th scope="col">{t('model.colThreshold')}</th>
          </tr>
        </thead>
        <tbody>
          <Row name={t('model.r1')} value={standing.r1} target={standing.targets.r1} />
          <Row name={t('model.r2')} value={standing.r2} target={standing.targets.r2} />
          <Row
            name={t('model.errorRate')}
            value={standing.error_rate}
            target={standing.targets.error_rate}
            lowerIsBetter
          />
        </tbody>
      </table>

      <dl>
        <dt>{t('model.declared')}</dt>
        <dd>{standing.declared}</dd>
        <dt>{t('model.predicted')}</dt>
        <dd>{standing.predicted}</dd>
        <dt>{t('model.matched')}</dt>
        <dd>{standing.matched}</dd>
      </dl>

      {standing.shortfall.length > 0 && (
        <section aria-labelledby="manca-titolo">
          <h3 id="manca-titolo">{t('model.shortfall')}</h3>
          <ul role="list">
            {standing.shortfall.map((one) => (
              <li key={one}>{one}</li>
            ))}
          </ul>
        </section>
      )}

      <p>
        {standing.on_display ? (
          <button type="button" className="primario" disabled={busy} onClick={() => act(() => hideModel())}>
            {t('model.hide')}
          </button>
        ) : (
          <button
            type="button"
            className="primario"
            // L'idoneita' e' quella dichiarata dal servizio: la pagina non
            // confronta R1 con la soglia per conto proprio.
            disabled={busy || !standing.acceptable}
            onClick={() => act(() => showModel())}
          >
            {t('model.show')}
          </button>
        )}
      </p>
      {!standing.on_display && !standing.acceptable && (
        <p className="tenue">{t('model.notAcceptable')}</p>
      )}
    </main>
  )
}

function Row({
  name,
  value,
  target,
  lowerIsBetter = false,
}: {
  name: string
  value: number | null
  target: number | undefined
  lowerIsBetter?: boolean
}): JSX.Element {
  return (
    <tr>
      <th scope="row">{name}</th>
      <td>{value === null ? notMeasured() : share(value)}</td>
      <td>
        {target === undefined ? notMeasured() : `${lowerIsBetter ? '≤' : '≥'} ${share(target)}`}
      </td>
    </tr>
  )
}

function read(error: unknown, fallback: string): string {
  return error instanceof ServiceError ? error.message : fallback
}
